import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Modal, Button } from 'react-bootstrap';
import DoubtService from '../../services/DoubtService';
import type { Doubt, StudentSummary, DoubtStatistics, DoubtStatus } from '../../types/doubt.types';
import './DoubtsDashboard.css';

const DoubtsDashboard: React.FC = () => {
  const [doubts, setDoubts] = useState<Doubt[]>([]);
  const [studentSummaries, setStudentSummaries] = useState<StudentSummary[]>([]);
  const [statistics, setStatistics] = useState<DoubtStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<DoubtStatus | 'all'>('escalated');
  const [selectedDoubt, setSelectedDoubt] = useState<Doubt | null>(null);
  const [teacherResponse, setTeacherResponse] = useState('');
  const [activeTab, setActiveTab] = useState<'inbox' | 'analytics' | 'students'>('inbox');
  const [modalMessage, setModalMessage] = useState<{ title: string; body: string } | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [selectedStudentName, setSelectedStudentName] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [selectedStatus, selectedStudentId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const doubtsPromise = selectedStudentId
        ? DoubtService.getStudentDoubts(selectedStudentId, {
            status: selectedStatus !== 'all' ? selectedStatus : undefined,
            limit: 100
          })
        : DoubtService.getAllDoubts({
            status: selectedStatus !== 'all' ? selectedStatus : undefined,
            limit: 100
          });

      const [doubtsData, summariesData, statsData] = await Promise.all([
        doubtsPromise,
        DoubtService.getStudentSummaries(),
        DoubtService.getStatistics({ dateFrom: getDateDaysAgo(30) })
      ]);

      setDoubts(doubtsData);
      setStudentSummaries(summariesData);
      setStatistics(statsData);
    } catch (error) {
      console.error('Failed to load doubt data:', error);
      setModalMessage({ title: 'Error', body: 'Failed to load doubts data: ' + (error as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const getDateDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString();
  };

  const handleResolveDoubt = async () => {
    if (!selectedDoubt || !teacherResponse.trim()) {
      setModalMessage({ title: 'Validation', body: 'Please enter a response.' });
      return;
    }

    try {
      const userData = localStorage.getItem('currentUser');
      const user = userData ? JSON.parse(userData) : null;
      const teacherId = user?.userId || user?.id || 1;

      await DoubtService.resolveDoubt(selectedDoubt.id, {
        teacherResponse: teacherResponse.trim(),
        resolvedBy: teacherId,
        resolutionNotes: 'Resolved by teacher'
      });

      setModalMessage({ title: 'Success', body: 'Doubt resolved successfully!' });
      setSelectedDoubt(null);
      setTeacherResponse('');
      loadData();
    } catch (error) {
      console.error('Failed to resolve doubt:', error);
      setModalMessage({ title: 'Error', body: 'Failed to resolve doubt. Please try again.' });
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleStudentClick = (summary: StudentSummary) => {
    setSelectedStudentId(summary.studentId);
    setSelectedStudentName(summary.studentName);
    setSelectedStatus('all');
    setActiveTab('inbox');
  };

  const clearStudentFilter = () => {
    setSelectedStudentId(null);
    setSelectedStudentName('');
    setSelectedStatus('escalated');
  };

  const getStatusBadge = (status: DoubtStatus) => {
    const badges = {
      pending: { color: 'orange', icon: <span>⏱</span>, text: 'Pending' },
      answered: { color: 'blue', icon: <span>✓</span>, text: 'Answered' },
      escalated: { color: 'red', icon: <span>⚠</span>, text: 'Escalated' },
      resolved: { color: 'green', icon: <span>✓</span>, text: 'Resolved' },
      closed: { color: 'gray', icon: <span>✓</span>, text: 'Closed' }
    };

    const badge = badges[status];
    return (
      <span className={`status-badge status-${badge.color}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  const getPriorityBadge = (priority?: string) => {
    if (!priority) return null;
    const colors = {
      low: 'gray',
      medium: 'blue',
      high: 'orange',
      urgent: 'red'
    };
    return <span className={`priority-badge priority-${colors[priority as keyof typeof colors]}`}>{priority}</span>;
  };

  if (loading) {
    return (
      <div className="doubts-dashboard">
        <div className="dashboard-header">
          <div className="header-title">
            <div className="skeleton skeleton-text" style={{width: 160, height: 22}} />
            <div className="skeleton skeleton-text" style={{width: 280, height: 13, marginTop: 4}} />
          </div>
          <div className="skeleton" style={{width: 90, height: 36, borderRadius: 6}} />
        </div>
        <div className="stats-grid">
          {[0, 1, 2, 3].map(i => (
            <div className="stat-card" key={i}>
              <div className="stat-card-top">
                <div className="skeleton skeleton-text" style={{width: 80, height: 13}} />
                <div className="skeleton" style={{width: 36, height: 36, borderRadius: 8}} />
              </div>
              <div className="skeleton skeleton-text" style={{width: 48, height: 28, marginBottom: 6}} />
              <div className="skeleton skeleton-text" style={{width: 70, height: 12}} />
            </div>
          ))}
        </div>
        <div style={{display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--border-default)'}}>
          {[100, 130, 120].map((w, i) => (
            <div key={i} className="skeleton skeleton-text" style={{width: w, height: 14, margin: '10px 16px'}} />
          ))}
        </div>
        <div style={{background: 'white', borderRadius: 10, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} style={{border: '1px solid var(--border-default)', borderRadius: 8, padding: '10px 14px', marginBottom: 8}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                <div className="skeleton" style={{width: 28, height: 28, borderRadius: '50%'}} />
                <div className="skeleton skeleton-text" style={{width: 120, height: 13}} />
                <div style={{marginLeft: 'auto'}}>
                  <div className="skeleton skeleton-text" style={{width: 60, height: 11}} />
                </div>
              </div>
              <div className="skeleton skeleton-text" style={{width: '90%', height: 13, marginBottom: 4}} />
              <div className="skeleton skeleton-text" style={{width: '60%', height: 13}} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="doubts-dashboard">
      <div className="dashboard-header">
        <div className="header-title">
          <div>
            <h1>Doubts Clinic</h1>
            <p>Manage student doubts and provide personalized support</p>
          </div>
        </div>
        <button className="refresh-btn" onClick={loadData}>
          ↻ Refresh
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-top">
              <p className="stat-label">Total Doubts</p>
              <div className="stat-icon stat-total"><span>📊</span></div>
            </div>
            <h3 className="stat-value">{statistics.overall.totalDoubts}</h3>
            <p className="stat-sub">{statistics.overall.pendingDoubts} pending</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-top">
              <p className="stat-label">Escalated</p>
              <div className="stat-icon stat-escalated"><span>⚠</span></div>
            </div>
            <h3 className="stat-value">{statistics.overall.escalatedDoubts}</h3>
            <p className="stat-sub">needs attention</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-top">
              <p className="stat-label">Resolved</p>
              <div className="stat-icon stat-resolved"><span>✓</span></div>
            </div>
            <h3 className="stat-value">{statistics.overall.resolvedDoubts}</h3>
            <p className="stat-sub">of {statistics.overall.totalEscalated} escalated</p>
          </div>
          <div className="stat-card">
            <div className="stat-card-top">
              <p className="stat-label">AI Satisfaction</p>
              <div className="stat-icon stat-satisfaction"><span>📈</span></div>
            </div>
            <h3 className="stat-value">{statistics.overall.totalDoubts > 0 ? Math.round((statistics.overall.helpfulCount / statistics.overall.totalDoubts) * 100) : 0}%</h3>
            <p className="stat-sub">{statistics.overall.helpfulCount} helpful</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab ${activeTab === 'inbox' ? 'active' : ''}`}
          onClick={() => setActiveTab('inbox')}
        >
          <span style={{fontSize: "24px"}}>❓</span> Doubts Inbox ({doubts.filter(d => d.status === 'escalated').length})
        </button>
        <button
          className={`tab ${activeTab === 'students' ? 'active' : ''}`}
          onClick={() => setActiveTab('students')}
        >
          <span style={{fontSize: "inherit"}}>👤</span> Student Overview ({studentSummaries.length})
        </button>
        <button
          className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <span style={{fontSize: "inherit"}}>📚</span> Topic Analytics
        </button>
      </div>

      {/* Inbox Tab */}
      {activeTab === 'inbox' && (
        <div className="doubts-section">
          <div className="section-header">
            <h2>{selectedStudentId ? `${selectedStudentName}'s Doubts` : 'Doubts Inbox'}</h2>
            <div className="filters">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as DoubtStatus | 'all')}
                className="status-filter"
              >
                <option value="all">All Status</option>
                <option value="escalated">Escalated</option>
                <option value="pending">Pending</option>
                <option value="answered">Answered</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>

          {selectedStudentId && (
            <div className="student-filter-banner">
              <span>Showing doubts from <strong>{selectedStudentName}</strong></span>
              <button className="clear-filter-btn" onClick={clearStudentFilter}>Clear Filter</button>
            </div>
          )}

          <div className="doubts-list">
            {doubts.length === 0 ? (
              <div className="empty-state">
                <span style={{fontSize: "inherit"}}>✓</span>
                <p>No doubts found</p>
              </div>
            ) : (
              doubts.map((doubt) => (
                <div
                  key={doubt.id}
                  className={`doubt-card ${doubt.status === 'escalated' ? 'escalated' : ''}`}
                  onClick={() => setSelectedDoubt(doubt)}
                >
                  <div className="doubt-header">
                    <div className="student-info">
                      <span className="student-avatar">
                        {doubt.studentName?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                      <strong>{doubt.studentName}</strong>
                      {doubt.courseName && (
                        <span className="doubt-context-inline">
                          {doubt.courseName}{doubt.topicName ? ` › ${doubt.topicName}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="doubt-meta">
                      {getStatusBadge(doubt.status)}
                      {getPriorityBadge(doubt.priority)}
                      <span className="doubt-time">{formatDate(doubt.createdAt)}</span>
                    </div>
                  </div>

                  <p className="doubt-question-text">{doubt.question}</p>

                  {doubt.escalationReason && (
                    <div className="escalation-reason">
                      <span>⚠</span> <em>{doubt.escalationReason}</em>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Students Tab */}
      {activeTab === 'students' && (
        <div className="students-section">
          <h2>Student Doubt Summaries</h2>
          <div className="students-grid">
            {studentSummaries.map((summary) => (
              <div key={summary.studentId} className="student-card" onClick={() => handleStudentClick(summary)}>
                <div className="student-header">
                  <span className="student-avatar">
                    {summary.studentName?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                  <strong>{summary.studentName}</strong>
                </div>
                <div className="student-stats">
                  <div className="stat-row">
                    <span>Total Doubts:</span>
                    <strong>{summary.totalDoubts}</strong>
                  </div>
                  <div className="stat-row">
                    <span>Escalated:</span>
                    <strong className="escalated-count">{summary.escalatedDoubts}</strong>
                  </div>
                  <div className="stat-row">
                    <span>Resolved:</span>
                    <strong className="resolved-count">{summary.resolvedDoubts}</strong>
                  </div>
                  {summary.avgRating && (
                    <div className="stat-row">
                      <span>Avg Rating:</span>
                      <strong>{summary.avgRating.toFixed(1)} / 5</strong>
                    </div>
                  )}
                  <div className="stat-row small">
                    <span>Last Doubt:</span>
                    <span>{formatDate(summary.lastDoubtDate)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && statistics && (
        <div className="analytics-section">
          <h2>Topic Analytics</h2>
          <p className="section-description">Topics with most doubts in the last 30 days</p>

          <div className="topics-list">
            {statistics.topTopics.map((topic, index) => (
              <div key={index} className="topic-card">
                <div className="topic-rank">{index + 1}</div>
                <div className="topic-info">
                  <strong>{topic.topicName || 'General'}</strong>
                  <span className="course-name">{topic.courseName}</span>
                </div>
                <div className="topic-stats">
                  <div className="topic-count">
                    <span style={{fontSize: "24px"}}>❓</span>
                    <span>{topic.doubtCount} doubts</span>
                  </div>
                  {topic.escalatedCount > 0 && (
                    <div className="topic-escalated">
                      <span style={{fontSize: "inherit"}}>⚠</span>
                      <span>{topic.escalatedCount} escalated</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Doubt Detail Modal */}
      {selectedDoubt && (
        <div className="modal-overlay" role="presentation" onClick={() => setSelectedDoubt(null)}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="doubt-details-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="doubt-details-title">Doubt Details</h3>
              <button className="close-btn" onClick={() => setSelectedDoubt(null)}>
                <span>×</span>
              </button>
            </div>

            <div className="modal-body">
              <div className="doubt-detail-section">
                <div className="detail-row">
                  <span className="detail-label">Student:</span>
                  <strong>{selectedDoubt.studentName}</strong>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Course:</span>
                  <span>{selectedDoubt.courseName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Topic:</span>
                  <span>{selectedDoubt.topicName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  {getStatusBadge(selectedDoubt.status)}
                </div>
                <div className="detail-row">
                  <span className="detail-label">Created:</span>
                  <span>{new Date(selectedDoubt.createdAt).toLocaleString()}</span>
                </div>
              </div>

              <div className="conversation-section">
                <h4>Question:</h4>
                <div className="question-box">{selectedDoubt.question}</div>

                {selectedDoubt.aiResponse && (
                  <>
                    <h4>AI Response:</h4>
                    <div className="ai-response-box"><ReactMarkdown>{selectedDoubt.aiResponse}</ReactMarkdown></div>
                  </>
                )}

                {selectedDoubt.conversationHistory && selectedDoubt.conversationHistory.length > 0 && (
                  <>
                    <h4>Conversation History:</h4>
                    <div className="conversation-history">
                      {selectedDoubt.conversationHistory.map((msg, idx) => (
                        <div key={idx} className={`dd-message ${msg.role === 'user' ? 'dd-message-user' : 'dd-message-assistant'}`}>
                          <strong>{msg.role === 'user' ? 'Student' : 'AI'}:</strong>
                          {msg.role === 'assistant' ? (
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          ) : (
                            <p>{msg.content}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {selectedDoubt.status === 'escalated' && !selectedDoubt.teacherResponse && (
                  <div className="teacher-response-section">
                    <h4>Your Response:</h4>
                    <textarea
                      value={teacherResponse}
                      onChange={(e) => setTeacherResponse(e.target.value)}
                      placeholder="Enter your response to help the student..."
                      rows={6}
                      className="response-textarea"
                    />
                    <button className="resolve-btn" onClick={handleResolveDoubt}>
                      <span>➤</span> Send Response & Resolve
                    </button>
                  </div>
                )}

                {selectedDoubt.teacherResponse && (
                  <>
                    <h4>Teacher Response:</h4>
                    <div className="teacher-response-box"><ReactMarkdown>{selectedDoubt.teacherResponse}</ReactMarkdown></div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal (replaces alert()) */}
      <Modal show={!!modalMessage} onHide={() => setModalMessage(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalMessage?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage?.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setModalMessage(null)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DoubtsDashboard;
